import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PaperAirplaneIcon,
  MicrophoneIcon,
  StopIcon,
  TrashIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  Bars3Icon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '../lib/apiClient';
import InsightResultCard from '../components/insights/InsightResultCard';
import ChatActionBar from '../components/chat/ChatActionBar';
import { EmailDraftModal } from '../components/EmailDraftModal';
import type { ChatMessage, ChatSendResponse, SuggestedAction, ChatLocationState } from '../types/chat';

interface ChatSession {
  session_id: string;
  title: string;
  last_message_at: string;
  message_count: number;
}

interface EmailDraftData {
  subject: string;
  body: string;
  customer_name: string;
  email_type: string;
  draft_id?: string;
  customer_id?: string;
}

const SUGGESTED_PROMPTS = [
  { icon: '🛰️', label: 'Which customers need attention this week?' },
  { icon: '⚠️', label: 'Which accounts are at renewal risk?' },
  { icon: '📖', label: 'Write a value narrative for Apple' },
  { icon: '📊', label: 'How is our portfolio doing?' },
];

function parseUtc(iso?: string): Date | null {
  if (!iso) return null;
  // Backend stores naive UTC isoformat (no tz). Treat as UTC.
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
}

function relativeTime(iso?: string): string {
  const d = parseUtc(iso);
  if (!d || isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

const EdytChatPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const activeSessionIdRef = useRef<string | null>(null);
  const [input, setInput] = useState('');
  const [recording, setRecording] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [emailDraftData, setEmailDraftData] = useState<EmailDraftData | null>(null);
  const [showEmailDraftModal, setShowEmailDraftModal] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sendMutationRef = useRef<((message: string) => void) | null>(null);
  const lastConsumedSearchMessage = useRef<string | null>(null);

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['chat-sessions'],
    queryFn: () => (apiClient as any).getChatSessions(),
  });

  const { data: sessionDetail, isLoading: messagesLoading } = useQuery({
    queryKey: ['chat-session', activeSessionId],
    queryFn: () => (apiClient as any).getChatSession(activeSessionId!),
    enabled: !!activeSessionId,
  });

  const sessions: ChatSession[] = sessionsData?.sessions || [];
  const messages: ChatMessage[] = useMemo(
    () => sessionDetail?.messages || [],
    [sessionDetail]
  );

  const activeSession = sessions.find((s) => s.session_id === activeSessionId);

  const handleDispatchSideEffects = useCallback((dispatch: Record<string, unknown>) => {
    if (dispatch?.action === 'show_modal' && dispatch?.modal_type === 'email_draft' && dispatch?.data) {
      setEmailDraftData(dispatch.data as EmailDraftData);
      setShowEmailDraftModal(true);
    }
  }, []);

  const sendMutation = useMutation<ChatSendResponse, Error, string>({
    mutationFn: (message: string) =>
      apiClient.sendChatMessage({
        message,
        session_id: activeSessionIdRef.current || undefined,
      }),
    onSuccess: (data) => {
      if (!activeSessionId) {
        setActiveSessionId(data.session_id);
      }
      setPendingUserMessage(null);
      handleDispatchSideEffects(data.dispatch);
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['chat-session', data.session_id] });
    },
    onError: () => {
      setPendingUserMessage(null);
    },
  });
  sendMutationRef.current = sendMutation.mutate;

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  const actionMutation = useMutation<ChatSendResponse, Error, SuggestedAction>({
    mutationFn: (action: SuggestedAction) =>
      apiClient.sendChatAction({
        session_id: activeSessionIdRef.current || undefined,
        action,
      }),
    onSuccess: (data) => {
      if (!activeSessionId) {
        setActiveSessionId(data.session_id);
      }
      setPendingUserMessage(null);
      handleDispatchSideEffects(data.dispatch);
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['chat-session', data.session_id] });
    },
    onError: () => {
      setPendingUserMessage(null);
    },
  });

  const handleExportQbr = useCallback(async (reportId: string) => {
    try {
      const blob = await (apiClient as any).exportQBRToPDF(reportId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qbr-${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Export errors surface via browser; user can retry from QBR page
    }
  }, []);

  const handleAction = useCallback(
    (action: SuggestedAction) => {
      if (action.kind === 'disabled') return;

      if (action.kind === 'send_message') {
        setPendingUserMessage(action.label);
        sendMutation.mutate(action.label);
        return;
      }

      if (action.kind === 'navigate' && action.path) {
        navigate(action.path);
        return;
      }

      if (action.kind === 'export_qbr' && action.report_id) {
        handleExportQbr(action.report_id);
        return;
      }

      if (action.kind === 'run_tool') {
        setPendingUserMessage(action.label);
        actionMutation.mutate(action);
      }
    },
    [actionMutation, handleExportQbr, navigate, sendMutation]
  );

  const isBusy = sendMutation.isPending || actionMutation.isPending;

  const deleteMutation = useMutation({
    mutationFn: (sessionId: string) => (apiClient as any).deleteChatSession(sessionId),
    onSuccess: (_, sessionId) => {
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
    },
  });

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sendMutation.isPending, pendingUserMessage]);

  // Global search bar → Ask Edyt: auto-send initial message in a fresh session
  useEffect(() => {
    const state = location.state as ChatLocationState | null;
    const msg = state?.initialMessage?.trim();
    if (!msg || !state?.fromSearch) return;
    if (lastConsumedSearchMessage.current === msg) return;
    if (sendMutation.isPending || actionMutation.isPending) return;

    lastConsumedSearchMessage.current = msg;
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    setInput('');
    setPendingUserMessage(msg);
    sendMutationRef.current?.(msg);

    navigate(location.pathname, { replace: true, state: {} });
  }, [
    actionMutation.isPending,
    location.pathname,
    location.state,
    navigate,
    sendMutation.isPending,
  ]);

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
    }
  }, [input]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isBusy) return;
    setInput('');
    setPendingUserMessage(text);
    sendMutation.mutate(text);
  }, [input, isBusy, sendMutation]);

  const handleNewChat = useCallback(() => {
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    setInput('');
    setPendingUserMessage(null);
    setShowSessions(false);
    lastConsumedSearchMessage.current = null;
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        try {
          const result = await (apiClient as any).transcribeVoice(blob);
          if (result.transcript) {
            setInput((prev) => (prev ? `${prev} ${result.transcript}` : result.transcript));
          }
        } catch {
          // ignore transcribe errors
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      // mic permission denied
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const isEmptyThread =
    !activeSessionId && !sendMutation.isPending && !pendingUserMessage;

  const sessionListContent = (
    <>
      <div className="p-3">
        <button
          type="button"
          onClick={handleNewChat}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
        >
          <PlusIcon className="h-4 w-4" />
          New chat
        </button>
      </div>
      <div className="px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Recent
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
        {sessionsLoading && (
          <div className="space-y-2 p-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-9 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        )}
        {!sessionsLoading && sessions.length === 0 && (
          <p className="px-2 py-3 text-sm text-muted-foreground">No conversations yet.</p>
        )}
        {sessions.map((s) => (
          <div
            key={s.session_id}
            className={`group flex items-center gap-1 rounded-lg px-2 py-2 text-sm transition-colors ${
              activeSessionId === s.session_id
                ? 'bg-primary/10 text-primary'
                : 'text-foreground hover:bg-muted'
            }`}
          >
            <button
              type="button"
              className="flex min-w-0 flex-1 flex-col items-start text-left"
              onClick={() => {
                setActiveSessionId(s.session_id);
                setShowSessions(false);
              }}
            >
              <span className="w-full truncate font-medium">{s.title || 'Chat'}</span>
              <span className="text-xs text-muted-foreground">
                {relativeTime(s.last_message_at)}
              </span>
            </button>
            <button
              type="button"
              className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                deleteMutation.mutate(s.session_id);
              }}
              title="Delete conversation"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="-mx-4 -my-4 flex h-[calc(100vh-4rem)] overflow-hidden bg-background sm:-mx-6 sm:-my-6 lg:-mx-8 lg:-my-8">
      {/* Mobile backdrop */}
      {showSessions && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={() => setShowSessions(false)}
        />
      )}

      {/* Session sidebar */}
      <aside
        className={`${
          showSessions ? 'flex' : 'hidden'
        } absolute inset-y-0 left-0 z-30 w-72 flex-col border-r border-border bg-card md:relative md:flex`}
      >
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary" />
          <h2 className="font-semibold text-foreground">Ask Edyt</h2>
        </div>
        {sessionListContent}
      </aside>

      {/* Main thread */}
      <main className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <div className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-border bg-card/60 px-4 backdrop-blur">
          <button
            type="button"
            onClick={() => setShowSessions(true)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted md:hidden"
            title="Conversations"
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-semibold text-foreground">
              {activeSession?.title || 'New conversation'}
            </h1>
          </div>
          <button
            type="button"
            onClick={handleNewChat}
            className="hidden items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted md:inline-flex"
          >
            <PlusIcon className="h-4 w-4" />
            New chat
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-4 py-6">
            {isEmptyThread ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-purple-500 shadow-lg">
                  <SparklesIcon className="h-7 w-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Ask Edyt anything</h2>
                <p className="mt-2 max-w-md text-muted-foreground">
                  Radar, risk, narratives, digests, and account questions — all in one conversation.
                </p>
                <div className="mt-8 grid w-full gap-3 sm:grid-cols-2">
                  {SUGGESTED_PROMPTS.map((prompt) => (
                    <button
                      key={prompt.label}
                      type="button"
                      className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left text-sm text-foreground transition-all hover:border-primary/50 hover:shadow-sm"
                      onClick={() => {
                        setInput(prompt.label);
                        textareaRef.current?.focus();
                      }}
                    >
                      <span className="text-xl">{prompt.icon}</span>
                      <span>{prompt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {messagesLoading && activeSessionId && (
                  <div className="space-y-4">
                    <div className="ml-auto h-10 w-2/3 animate-pulse rounded-2xl bg-muted" />
                    <div className="h-24 w-5/6 animate-pulse rounded-2xl bg-muted" />
                  </div>
                )}

                {messages.map((msg, idx) => (
                  <MessageRow
                    key={msg.message_id || idx}
                    message={msg}
                    onAction={handleAction}
                    actionsDisabled={isBusy}
                  />
                ))}

                {pendingUserMessage && (
                  <MessageRow
                    message={{ role: 'user', content: pendingUserMessage }}
                    onAction={() => {}}
                  />
                )}

                {isBusy && (
                  <div className="flex items-start gap-3">
                    <Avatar />
                    <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-muted px-4 py-3">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" />
                    </div>
                  </div>
                )}

                {sendMutation.isError && (
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => pendingUserMessage && sendMutation.mutate(pendingUserMessage)}
                      className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive hover:bg-destructive/20"
                    >
                      Failed to send. Tap to retry.
                    </button>
                  </div>
                )}

                <div ref={threadEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div className="flex-shrink-0 border-t border-border bg-card/80 px-4 py-3 backdrop-blur">
          <div className="mx-auto w-full max-w-3xl">
            <div className="flex items-end gap-2 rounded-2xl border border-border bg-background p-2 shadow-sm focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/30">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                rows={1}
                placeholder="Ask Edyt anything…"
                className="max-h-44 flex-1 resize-none bg-transparent px-2 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl transition-colors ${
                  recording
                    ? 'bg-red-500/10 text-red-500'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
                title={recording ? 'Stop recording' : 'Voice input'}
              >
                {recording ? <StopIcon className="h-5 w-5" /> : <MicrophoneIcon className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || isBusy}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40"
                title="Send"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              EDYT can make mistakes. Verify important account decisions.
            </p>
          </div>
        </div>
      </main>

      <EmailDraftModal
        isOpen={showEmailDraftModal}
        onClose={() => {
          setShowEmailDraftModal(false);
          setEmailDraftData(null);
        }}
        emailData={emailDraftData}
      />
    </div>
  );
};

const Avatar: React.FC = () => (
  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-purple-500 text-xs font-bold text-white">
    E
  </div>
);

const MessageRow: React.FC<{
  message: ChatMessage;
  onAction: (action: SuggestedAction) => void;
  actionsDisabled?: boolean;
}> = ({ message, onAction, actionsDisabled = false }) => {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground">
          {message.content}
        </div>
      </div>
    );
  }

  const hasPayload = !!message.payload;
  const showTextBubble = !hasPayload && message.content;

  return (
    <div className="flex items-start gap-3">
      <Avatar />
      <div className="min-w-0 flex-1">
        {hasPayload ? (
          <div className="rounded-2xl rounded-tl-sm border border-border bg-card p-4">
            <InsightResultCard payload={message.payload} compact showOpenFull />
          </div>
        ) : showTextBubble ? (
          <div className="inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-muted px-4 py-2.5 text-sm text-foreground">
            {message.content}
          </div>
        ) : null}
        {message.suggested_actions && message.suggested_actions.length > 0 && (
          <ChatActionBar
            actions={message.suggested_actions}
            onAction={onAction}
            disabled={actionsDisabled}
          />
        )}
      </div>
    </div>
  );
};

export default EdytChatPage;
